import styled from "styled-components";

import LabelSection from "src/components/core/LabelSection";
import Button from "src/components/core/Button";
import { Typography } from "@mui/material";
import Dropdown from "src/components/core/Dropdown";

import AddIcon from "@mui/icons-material/Add";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { MoreHoriz, Edit } from "@mui/icons-material";

import { arrayMove } from "@dnd-kit/sortable";
import DraggableBoard, {
  DraggableItem,
} from "src/components/core/DraggableBoard";

import BannerAddDialog from "./BannerAddDialog";

import { useDialog } from "src/context/DialogContext";

import font from "src/styles/font";
import { Black, DarkGrey, White } from "src/styles/color";
import { hexToRgba } from "src/common/color";

import useBanner from "src/hooks/useBanner";

const S = {};

S.BoxWrapper = styled.div`
  width: 100%;
  height: auto;
  padding: 32px;
  border-radius: 32px;
  background-color: ${White};
  box-shadow: 0px 4px 20px ${hexToRgba(Black, 0.1)};
  justify-content: center;
  align-items: center;
  margin-bottom: 32px;
`;

S.BannerListContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

S.SubBannerListContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 16px;
`;

S.BannerItemWrapper = styled(DraggableItem)`
  position: relative;
  width: 100%;
  padding-top: calc(100% / (844 / 380));
  background-color: #f3f1f2;
  border-radius: 16px;
  background-image: url(${(props) => props.$backgroundImage});
  background-size: cover;
  background-position: center;
`;

S.SubBannerItemWrapper = styled(DraggableItem)`
  position: relative;
  width: 180px;
  height: 180px;
  background-color: #f3f1f2;
  border-radius: 16px;
  background-image: url(${(props) => props.$backgroundImage});
  background-size: cover;
  background-position: center;
`;

S.HoverContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  opacity: 0;
  transition: all 0.2s;

  &:hover {
    opacity: 1;
  }
`;

S.ItemBackdrop = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 16px;
`;

S.ItemActionMenu = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  padding: 8px;
`;

const BannerDesktop = () => {
  const {
    banners: desktopMainBanners,
    setBanners: setDesktopMainBanners,
    addBanner: addDesktopMainBanner,
    fetchBanners: fetchDesktopMainBanners,
    updateBanner: updateDesktopMainBanner,
    updateBanners: updateDesktopMainBanners,
    toggleVisibility: toggleVisibilityDesktopMainBanners,
    deleteBanner: deleteDesktopMainBanner,
  } = useBanner({
    bannerKey: "home",
    resource: "main_desktop",
  });

  const {
    banners: desktopSubBanners,
    setBanners: setDesktopSubBanners,
    addBanner: addDesktopSubBanner,
    fetchBanners: fetchDesktopSubBanners,
    updateBanner: updateDesktopSubBanner,
    updateBanners: updateDesktopSubBanners,
    toggleVisibility: toggleVisibilityDesktopSubBanners,
    // deleteBanner: deleteDesktopSubBanner,
  } = useBanner({
    bannerKey: "home",
    resource: "sub_desktop",
  });

  const { openDialog, closeDialog, closeAllDialogs } = useDialog();

  return (
    <S.BoxWrapper>
      <LabelSection
        title="แบนเนอร์ Desktop"
        headerStyle={{
          marginBottom: 0,
        }}
      >
        <div
          style={{
            marginBlock: 32,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div>
            <Typography
              style={{
                color: Black,
                fontSize: font.size.sm,
              }}
            >
              <strong>แบนเนอร์หลัก :</strong> เพิ่มแบนเนอร์ได้สูงสุด 8 รูป
              ขนาดไฟล์ 844x380 px ไม่เกิน 4 MB
            </Typography>
            <Typography
              style={{
                color: DarkGrey,
                fontSize: font.size.sm,
              }}
            >
              คุณสามารถลากแบนเนอร์เพื่อสลับตำแหน่งได้
            </Typography>
          </div>
          <Button
            startIcon={<AddIcon />}
            variant="secondary"
            size="large"
            sx={{
              minWidth: 200,
            }}
            onClick={() => {
              openDialog({
                type: "custom",
                customDialog: (
                  <BannerAddDialog
                    onAddBanner={addDesktopMainBanner}
                    onClose={() => closeDialog()}
                    onSuccess={() => {
                      fetchDesktopMainBanners();
                      closeAllDialogs();
                    }}
                    dimension={{
                      width: 844,
                      height: 380,
                    }}
                  />
                ),
              });
            }}
            disabled={desktopMainBanners.length >= 8}
          >
            เพิ่มแบนเนอร์
          </Button>
        </div>
        <DraggableBoard
          items={desktopMainBanners}
          onDragEnd={async ({ active, over }) => {
            if (active.id !== over?.id) {
              const oldIndex = desktopMainBanners.findIndex(
                (item) => item._id === active.id,
              );
              const newIndex = desktopMainBanners.findIndex(
                (item) => item._id === over.id,
              );

              const newReArranged = arrayMove(
                desktopMainBanners,
                oldIndex,
                newIndex,
              );

              setDesktopMainBanners(newReArranged); // optimistic update

              try {
                await updateDesktopMainBanners(newReArranged);
                await fetchDesktopMainBanners();
              } catch (err) {
                setDesktopMainBanners(desktopMainBanners); // if error, revert to original order
                console.error(err);
              }
            }
          }}
          Components={{
            DroppableParent: ({ items, children }) => {
              return (
                <S.BannerListContainer>
                  {children({ items })}
                </S.BannerListContainer>
              );
            },
            DraggableItem: (props) => {
              const { items, item, ...rest } = props; // Destructuring items to prevent this spreading to html element
              return (
                <S.BannerItemWrapper $backgroundImage={item?.image} {...rest}>
                  {!item?.is_public && (
                    <S.ItemBackdrop>
                      <VisibilityOffIcon
                        sx={{
                          color: White,
                        }}
                      />
                    </S.ItemBackdrop>
                  )}
                  <S.HoverContainer>
                    {item?.is_public && <S.ItemBackdrop />}
                    <S.ItemActionMenu>
                      <Dropdown
                        renderButton={(props) => (
                          <IconButton
                            sx={{
                              color: White,
                              backgroundColor: hexToRgba(White, 0.4),
                            }}
                            {...props}
                          >
                            <MoreHoriz />
                          </IconButton>
                        )}
                        options={[
                          {
                            label: "แก้ไข",
                            startIcon: <Edit />,
                            onClick: () => {
                              openDialog({
                                type: "custom",
                                customDialog: (
                                  <BannerAddDialog
                                    initialValue={item}
                                    onUpdateBanner={updateDesktopMainBanner}
                                    onClose={() => closeDialog()}
                                    onSuccess={() => {
                                      fetchDesktopMainBanners();
                                      closeAllDialogs();
                                    }}
                                    dimension={{
                                      width: 1440,
                                      height: 540,
                                    }}
                                  />
                                ),
                              });
                            },
                          },
                          {
                            label: item?.is_public ? "ซ่อน" : "แสดง",
                            startIcon: item?.is_public ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            ),
                            onClick: () => {
                              openDialog({
                                type: "confirm",
                                title: `คุณต้องการ${
                                  item?.is_public ? "ปิด" : "เปิด"
                                }การมองเห็นใช่หรือไม่?`,
                                onConfirm: async () => {
                                  await toggleVisibilityDesktopMainBanners(
                                    item?.position,
                                  );
                                  fetchDesktopMainBanners();
                                },
                                maxWidth: 342,
                              });
                            },
                          },
                          {
                            label: "ลบ",
                            startIcon: <DeleteOutlineRoundedIcon />,
                            onClick: () => {
                              openDialog({
                                type: "confirm",
                                title: "คุณต้องการลบใช่หรือไม่?",
                                onConfirm: async () => {
                                  await deleteDesktopMainBanner(item?.position);
                                  fetchDesktopMainBanners();
                                },
                                maxWidth: 342,
                              });
                            },
                          },
                        ]}
                      />
                    </S.ItemActionMenu>
                  </S.HoverContainer>
                </S.BannerItemWrapper>
              );
            },
          }}
        />
        <div
          style={{
            marginBlock: 32,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div>
            <Typography
              style={{
                color: Black,
                fontSize: font.size.sm,
              }}
            >
              <strong>แบนเนอร์รอง :</strong> เพิ่มแบนเนอร์ได้สูงสุด 4 รูป
              ขนาดไฟล์ 180x180 px ไม่เกิน 2 MB
            </Typography>
            <Typography
              style={{
                color: DarkGrey,
                fontSize: font.size.sm,
              }}
            >
              คุณสามารถลากแบนเนอร์เพื่อสลับตำแหน่งได้
            </Typography>
          </div>
          <Button
            startIcon={<AddIcon />}
            variant="secondary"
            size="large"
            sx={{
              minWidth: 200,
            }}
            onClick={() => {
              openDialog({
                type: "custom",
                customDialog: (
                  <BannerAddDialog
                    onAddBanner={addDesktopSubBanner}
                    onClose={() => closeDialog()}
                    onSuccess={() => {
                      fetchDesktopSubBanners();
                      closeAllDialogs();
                    }}
                    dimension={{
                      width: 180,
                      height: 180,
                    }}
                  />
                ),
              });
            }}
            disabled={desktopSubBanners.length >= 4}
          >
            เพิ่มแบนเนอร์
          </Button>
        </div>
        <DraggableBoard
          items={desktopSubBanners}
          onDragEnd={async ({ active, over }) => {
            if (active.id !== over?.id) {
              const oldIndex = desktopSubBanners.findIndex(
                (item) => item._id === active.id,
              );
              const newIndex = desktopSubBanners.findIndex(
                (item) => item._id === over.id,
              );

              const newReArranged = arrayMove(
                desktopSubBanners,
                oldIndex,
                newIndex,
              );

              setDesktopSubBanners(newReArranged); // optimistic update

              try {
                await updateDesktopSubBanners(newReArranged);
                await fetchDesktopSubBanners();
              } catch (err) {
                setDesktopSubBanners(desktopSubBanners); // if error, revert to original order
                console.error(err);
              }
            }
          }}
          Components={{
            DroppableParent: ({ items, children }) => {
              return (
                <S.SubBannerListContainer>
                  {children({ items })}
                </S.SubBannerListContainer>
              );
            },
            DraggableItem: (props) => {
              const { items, item, ...rest } = props; // Destructuring items to prevent this spreading to html element
              return (
                <S.SubBannerItemWrapper
                  $backgroundImage={item?.image}
                  {...rest}
                >
                  {!item?.is_public && (
                    <S.ItemBackdrop>
                      <VisibilityOffIcon
                        sx={{
                          color: White,
                        }}
                      />
                    </S.ItemBackdrop>
                  )}
                  <S.HoverContainer>
                    {item?.is_public && <S.ItemBackdrop />}
                    <S.ItemActionMenu>
                      <Dropdown
                        renderButton={(props) => (
                          <IconButton
                            sx={{
                              color: White,
                              backgroundColor: hexToRgba(White, 0.4),
                            }}
                            {...props}
                          >
                            <MoreHoriz />
                          </IconButton>
                        )}
                        options={[
                          {
                            label: "แก้ไข",
                            startIcon: <Edit />,
                            onClick: () => {
                              openDialog({
                                type: "custom",
                                customDialog: (
                                  <BannerAddDialog
                                    initialValue={item}
                                    onUpdateBanner={updateDesktopSubBanner}
                                    onClose={() => closeDialog()}
                                    onSuccess={() => {
                                      fetchDesktopSubBanners();
                                      closeAllDialogs();
                                    }}
                                    dimension={{
                                      width: 180,
                                      height: 180,
                                    }}
                                  />
                                ),
                              });
                            },
                          },
                          {
                            label: item?.is_public ? "ซ่อน" : "แสดง",
                            startIcon: item?.is_public ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            ),
                            onClick: () => {
                              openDialog({
                                type: "confirm",
                                title: `คุณต้องการ${
                                  item?.is_public ? "ปิด" : "เปิด"
                                }การมองเห็นใช่หรือไม่?`,
                                onConfirm: async () => {
                                  await toggleVisibilityDesktopSubBanners(
                                    item?.position,
                                  );
                                  fetchDesktopSubBanners();
                                },
                                maxWidth: 342,
                              });
                            },
                          },
                          // {
                          //   label: "ลบ",
                          //   startIcon: <DeleteOutlineRoundedIcon />,
                          //   onClick: () => {
                          //     openDialog({
                          //       type: "confirm",
                          //       title: "คุณต้องการลบใช่หรือไม่?",
                          //       onConfirm: async () => {
                          //         await deleteDesktopSubBanner(item?.position);
                          //         fetchDesktopSubBanners();
                          //       },
                          //       maxWidth: 342,
                          //     });
                          //   },
                          // },
                        ]}
                      />
                    </S.ItemActionMenu>
                  </S.HoverContainer>
                </S.SubBannerItemWrapper>
              );
            },
          }}
        />

        {/* <Typography
          sx={{
            mt: 3,
            alignSelf: "center",
            fontSize: font.size.sm,
            color: DarkGrey,
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          <InfoRounded
            sx={{
              color: Yellow,
              mr: 0.5,
            }}
          />{" "}
          เพิ่มแบนเนอร์ได้สูงสุด 12 รูป ขนาดไฟล์ 1440x540 px ไม่เกิน 4 MB
        </Typography> */}
      </LabelSection>
    </S.BoxWrapper>
  );
};

export default BannerDesktop;
